import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useVirtualizer } from '@tanstack/react-virtual';
import { BookOpen, ChevronDown, ChevronUp, Columns3, Filter, Search, X } from 'lucide-react';
import {
  useInfiniteScreener,
  flattenPages,
  totalFromPages,
  useScreenerFacets,
  useScreenerStats,
} from '../../api/screener-hooks.js';
import type { ScreenerRow } from '../../api/screener-client.js';
import { PanelFrame } from '../panel-frame.js';
import { EntityRowContextMenu } from '../entity-row-context-menu.js';
import { ENTITY_MIME } from '../dnd.js';
import { ScreenerPresetPicker } from '../screener-preset-picker.js';
import { ScreenerFilterModalV2 } from '../screener-filter-modal-v2.js';
import { useOpenMethodology } from '../use-open-methodology.js';
import {
  buildScreenerQuery,
  countActiveCategoricals,
  countActiveRanges,
  defaultCategoricalFilters,
  defaultRangeFilters,
  type CategoricalFilterKey,
  type CategoricalFilters,
  type RangeFilterKey,
  type RangeFilters,
  type RangeValue,
} from '../screener-filters.js';
import {
  FROZEN_NAME_DEFAULT_WIDTH,
  SCREENER_COLUMN_DEFS,
  defaultScreenerColumnState,
  renderCell,
  type ScreenerRightColumnId,
} from '../screener-columns.js';
import {
  useScreenerColumnConfig,
  type ScreenerColumnConfig,
  type ScreenerColumnConfigActions,
} from '../use-screener-column-config.js';
import { ColumnsMenu } from '../columns-menu.js';
import { ColumnHeaderCell } from '../column-header-cell.js';
import { useDockviewApi } from '../dockview-context.js';
import {
  useAppStore,
  useSetPanelEntity,
  usePanelEntityId,
  useGlobalCurrency,
  useRegionLevel,
  type PanelId,
  type TabId,
} from '../store.js';

// The platform-wide Screener — list view per Screener_and_Panel_Build_Spec.md §3.
// 11 columns: Entity (frozen) + 10 in the scrollable right pane.
// Data source: GET /screener (entities ⋈ entity_normalised_metrics);
// row identity is entities.id UUID, so watchlists / lists / presets all
// work uniformly across markets.
//
// UX patterns preserved from the previous Screener:
//   - Two-pane layout (frozen Entity column on the left, scrollable
//     right pane). Right pane scroll drives a transform on the left
//     so the two stay aligned.
//   - TanStack Virtual for the row list (100k+ rows perf-stable).
//   - IntersectionObserver sentinel for infinite-scroll pagination.
//   - Click-to-sort headers + asc/desc indicator.
//   - Keyboard nav: j/k or arrow up/down, g/G to top/end, / to focus
//     search, r marks the selected row rejected.
//   - Drag a row by mime ENTITY_MIME onto any list panel to add.
//   - Right-click for the EntityRowContextMenu (Add to watchlist,
//     Mark rejected, etc.).
//
// Scoped out of V1 (will land in a follow-up):
//   - Column resize / reorder / hide. Defaults from screener-columns.ts.

const ROW_HEIGHT = 26;
const COLUMN_CONFIG_KEY = 'corryk:screener-columns:v2';

type SortField =
  | 'company_name'
  | 'turnover_screener'
  | 'net_assets'
  | 'employees'
  | 'years_active'
  | 'subsegment'
  | 'region'
  | 'ownership_type'
  | 'quality_tier'
  | 'quality_direction'
  | 'origination_signal'
  | 'seller_readiness';

type SortKey = 'companyName' | ScreenerRightColumnId;

const SORT_KEY_TO_FIELD: Record<SortKey, SortField> = {
  companyName: 'company_name',
  countryCode: 'company_name', // country sort not supported server-side; fall back
  region: 'region',
  subsegment: 'subsegment',
  turnoverScreener: 'turnover_screener',
  turnoverConfidenceP: 'turnover_screener',
  netAssets: 'net_assets',
  employees: 'employees',
  ownershipType: 'ownership_type',
  tenure: 'ownership_type', // tenure not server-sortable; fall back
  qualityTier: 'quality_tier',
  originationSignal: 'origination_signal',
  sellerReadiness: 'seller_readiness',
};

// Coerce an initialState bag value (string | string[] | other) to a string[]
// for seeding categorical filters, or undefined when not present.
function asStrArray(v: unknown): string[] | undefined {
  if (Array.isArray(v)) {
    const out = v.filter((x): x is string => typeof x === 'string');
    return out.length ? out : undefined;
  }
  if (typeof v === 'string' && v) return [v];
  return undefined;
}

export function ScreenerPanel({
  tabId,
  panelId,
  initialState,
}: {
  tabId: TabId;
  panelId: PanelId;
  /** Optional pre-configuration set when a caller spawns the panel
   *  programmatically (e.g. the StatusBar quarantine badge opens a
   *  floating Screener with `{ quarantineOnly: true }`). Read once on
   *  mount to seed initial state; subsequent user interactions own
   *  the panel's state from then on. */
  initialState?: Record<string, unknown>;
}) {
  const { t } = useTranslation();

  // Column layout (order / width / visibility) is owned here so the toolbar
  // "Columns" manager and the table headers share one persisted instance.
  const [columnConfig, colActions] = useScreenerColumnConfig(COLUMN_CONFIG_KEY);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const columnsBtnRef = useRef<HTMLButtonElement>(null);
  const openMethodology = useOpenMethodology();

  // Filter + sort state. Seeded from initialState so a programmatically-
  // spawned Screener (the Market Opportunity drill, the quarantine badge…)
  // opens already filtered; the user owns the state from then on.
  const [categoricals, setCategoricals] = useState<CategoricalFilters>(() => {
    const base = defaultCategoricalFilters();
    const c = asStrArray(initialState?.['presetCountry']);
    const r = asStrArray(initialState?.['presetRegion']);
    // Mixed axis: canonical sub-segment labels AND fine specialty tokens share
    // this one set (buildScreenerQuery splits them back apart), so the Market
    // Opportunity drill can seed either kind of bucket through it — and the user
    // gets an editable chip rather than a filter locked into the wire query.
    const s = asStrArray(initialState?.['presetSubsegment']);
    return {
      ...base,
      country: c ? new Set(c) : base.country,
      region: r ? new Set(r) : base.region,
      subsegment: s ? new Set(s) : base.subsegment,
    };
  });
  const [ranges, setRanges] = useState<RangeFilters>(() => {
    const base = defaultRangeFilters();
    const min = initialState?.['presetTurnoverMin'];
    return typeof min === 'number'
      ? { ...base, turnover: [min, base.turnover[1]] as RangeValue }
      : base;
  });
  const [query, setQuery] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('turnoverScreener');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Operators (default) or the landlords of in-scope care homes — never mixed in one list.
  const [role, setRole] = useState<'operator' | 'landlord'>('operator');
  const [quarantineOnly] = useState<boolean>(
    typeof initialState?.['quarantineOnly'] === 'boolean'
      ? (initialState['quarantineOnly'] as boolean)
      : false,
  );
  // Legacy Market Opportunity drill axis — filtered entities.sub_segment, which
  // is populated in 6 of 27 markets, so outside those six it opened an empty
  // Screener. The drill now seeds the `subsegment` categorical instead (see the
  // filter state above). Still read here so a workspace layout persisted before
  // 2026-07-20 keeps behaving exactly as it did.
  const [presetSubCanonical] = useState<readonly string[] | undefined>(() => {
    const v = initialState?.['presetSubSegmentsCanonical'];
    return Array.isArray(v) && v.length ? (v as string[]) : undefined;
  });
  // Independent-only (no corporate parent) — Market Opportunity drill restricts
  // to the actionable bolt-on pool. Applied server-side.
  const [presetIndependentOnly] = useState<boolean>(
    initialState?.['presetIndependentOnly'] === true,
  );

  // Debounce the search so each keystroke doesn't refetch.
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedQ(query), 200);
    return () => window.clearTimeout(id);
  }, [query]);

  const setCategorical = (key: CategoricalFilterKey, next: Set<string>) => {
    setCategoricals((prev) => ({ ...prev, [key]: next }));
  };
  const setRange = (key: RangeFilterKey, next: RangeValue) => {
    setRanges((prev) => ({ ...prev, [key]: next }));
  };
  const clearAll = () => {
    setCategoricals(defaultCategoricalFilters());
    setRanges(defaultRangeFilters());
    setQuery('');
  };

  const activeFilterCount =
    countActiveCategoricals(categoricals) + countActiveRanges(ranges) + (debouncedQ ? 1 : 0);

  // Workspace-wide NUTS region grain — the region filter/display honour it, and
  // the facet list refetches so the dropdown offers regions at the same level.
  const regionLevel = useRegionLevel();

  // Compose the wire query from local state.
  const wireQuery = useMemo(
    () => ({
      ...buildScreenerQuery(categoricals, ranges, debouncedQ, SORT_KEY_TO_FIELD[sortKey], sortDir),
      level: regionLevel,
      ...(quarantineOnly ? { quarantineOnly: true } : {}),
      ...(presetSubCanonical ? { subSegmentCanonical: presetSubCanonical } : {}),
      ...(presetIndependentOnly ? { independentOnly: true } : {}),
      ...(role === 'landlord' ? { role: 'landlord' as const } : {}),
    }),
    [
      categoricals,
      ranges,
      debouncedQ,
      sortKey,
      sortDir,
      regionLevel,
      quarantineOnly,
      presetSubCanonical,
      presetIndependentOnly,
      role,
    ],
  );

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteScreener(wireQuery);
  const facetsQ = useScreenerFacets(regionLevel);
  const statsQ = useScreenerStats();

  const rows = useMemo(() => flattenPages(data), [data]);
  const total = totalFromPages(data);

  // No auto-fetch — we lazy-load on scroll instead. The first page
  // (1 000 rows) renders fast (~130 ms), and the ScreenerTable component
  // has an IntersectionObserver sentinel that fires `onLoadMore` when
  // the user scrolls within ~7 rows of the end. Previously every filter
  // change forced ~3.5 s of background fetching (16 sequential pages)
  // even though TanStack Virtual only renders the ~30 rows on screen.
  //
  // The first page's `total` field still drives the result-count badge,
  // so the "ENTIRE filtered set" guarantee is preserved at the count
  // level. Server-side sorting means the visible rows are always the
  // correct top-of-list per the active sortBy/sortDir. Any future
  // bulk-drag-to-list flow that operates on rows beyond what's loaded
  // needs to either (a) trigger fetchNextPage until everything's in
  // memory, or (b) call a "select-by-filter" server endpoint.

  // Saved-screener presets store filter state under a generic JSON blob.
  // We just round-trip our `categoricals + ranges + query + sort`.
  const currentPreset = useMemo<Record<string, unknown>>(
    () => ({
      v: 2,
      categoricals: Object.fromEntries(
        Object.entries(categoricals).map(([k, s]) => [k, Array.from(s)]),
      ),
      ranges,
      query,
      sortKey,
      sortDir,
    }),
    [categoricals, ranges, query, sortKey, sortDir],
  );
  function applyPreset(filter: Record<string, unknown>) {
    if (filter['v'] !== 2) return; // ignore old shape; old screener presets are v1
    const c = (filter['categoricals'] as Record<string, string[]>) ?? {};
    setCategoricals({
      country: new Set(c['country'] ?? []),
      region: new Set(c['region'] ?? []),
      subsegment: new Set(c['subsegment'] ?? []),
      clinicalSpecialism: new Set(c['clinicalSpecialism'] ?? []),
      backing: new Set(c['backing'] ?? []),
      ownershipType: new Set(c['ownershipType'] ?? []),
      tenure: new Set(c['tenure'] ?? []),
      qualityTier: new Set(c['qualityTier'] ?? []),
      // qualityDirection presets are silently dropped — the filter was
      // retired with the column (2026-07-22).
    });
    const r = filter['ranges'] as RangeFilters | undefined;
    if (r) setRanges(r);
    setQuery(typeof filter['query'] === 'string' ? filter['query'] : '');
    if (filter['sortKey']) setSortKey(filter['sortKey'] as SortKey);
    if (filter['sortDir']) setSortDir(filter['sortDir'] as 'asc' | 'desc');
  }

  // Row selection + panel transitions
  const selected = usePanelEntityId(tabId, panelId) ?? null;
  const setPanelEntity = useSetPanelEntity(tabId, panelId);
  const select = (id: string) => setPanelEntity(id);

  // Double-click on a row → open a NEW floating Company Overview pinned
  // to that entity. Different semantics from single-click (which only
  // broadcasts the entity to the channel): double-click is a deliberate
  // "give me an independent window on this company" gesture, so the new
  // panel is pinned and won't follow channel changes. Each double-click
  // cascades a fresh panel — multiple windows side-by-side is intended.
  const dockviewApiRef = useDockviewApi();
  const addPanelToStore = useAppStore((s) => s.actions.addPanel);
  const pinPanelAction = useAppStore((s) => s.actions.pinPanel);
  const setPinnedEntity = useAppStore((s) => s.actions.setPanelEntity);
  function openCompanyOverviewFor(entityId: string) {
    const api = dockviewApiRef.current;
    if (!api) return;
    const newId = addPanelToStore(tabId, {
      component: 'company-overview',
      title: 'Record',
    });
    // Pin first (decouples from the default channel) then set the
    // panel's entity directly — pinned panels' setPanelEntity writes
    // link.entityId in-place rather than broadcasting.
    pinPanelAction(tabId, newId);
    setPinnedEntity(tabId, newId, entityId);
    const cascade = api.panels.filter((p) => p.api.location.type === 'floating').length;
    const offset = 24 * cascade;
    api.addPanel({
      id: newId,
      component: 'company-overview',
      title: 'Record',
      params: { tabId, panelId: newId },
      floating: {
        position: { left: 200 + offset, top: 120 + offset },
        width: 720,
        height: 600,
      },
    });
  }

  const panelRootRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<string | null>(selected);
  selectedRef.current = selected;
  function onPanelKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const tag = (e.target as HTMLElement | null)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    if (rows.length === 0) return;
    const curr = selectedRef.current;
    const idx = curr ? rows.findIndex((r) => r.entityId === curr) : -1;
    const move = (delta: number) => {
      const next = Math.max(0, Math.min(rows.length - 1, (idx === -1 ? 0 : idx) + delta));
      const row = rows[next];
      if (row) {
        select(row.entityId);
        selectedRef.current = row.entityId;
      }
    };
    if (e.key === 'j' || e.key === 'ArrowDown') {
      e.preventDefault();
      move(1);
    } else if (e.key === 'k' || e.key === 'ArrowUp') {
      e.preventDefault();
      move(-1);
    } else if (e.key === 'g') {
      e.preventDefault();
      const first = rows[0];
      if (first) select(first.entityId);
    } else if (e.key === 'G') {
      e.preventDefault();
      const last = rows[rows.length - 1];
      if (last) select(last.entityId);
    } else if (e.key === '/') {
      e.preventDefault();
      const input = panelRootRef.current?.querySelector<HTMLInputElement>('input');
      input?.focus();
    }
  }

  function onSort(k: SortKey) {
    if (k === sortKey) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(k);
      // numeric / date-like columns default to descending; text to ascending
      const numeric =
        k === 'turnoverScreener' ||
        k === 'netAssets' ||
        k === 'employees' ||
        k === 'turnoverConfidenceP' ||
        k === 'originationSignal' ||
        k === 'sellerReadiness';
      setSortDir(numeric ? 'desc' : 'asc');
    }
  }

  return (
    <PanelFrame tabId={tabId} panelId={panelId} title={t('panels.screener.title')}>
      <div
        ref={panelRootRef}
        tabIndex={0}
        onKeyDown={onPanelKeyDown}
        style={{
          display: 'grid',
          gridTemplateRows: 'auto 1fr',
          height: '100%',
          minHeight: 0,
          outline: 'none',
        }}
      >
        {/* Filter bar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--s-2)',
            padding: 'var(--s-2) var(--s-3)',
            borderBottom: '1px solid var(--hairline)',
            background: 'var(--surface)',
          }}
        >
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--s-1)',
              fontSize: 'var(--t-body)',
            }}
          >
            <span style={{ color: 'var(--text-secondary)' }}>{t('panels.screener.roleLabel')}</span>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value === 'landlord' ? 'landlord' : 'operator')}
              aria-label={t('panels.screener.roleLabel')}
            >
              <option value="operator">{t('panels.screener.roleOperators')}</option>
              <option value="landlord">{t('panels.screener.roleLandlords')}</option>
            </select>
          </label>
          <ScreenerPresetPicker currentFilter={currentPreset} onApply={applyPreset} />
          <button
            type="button"
            onClick={() => setFiltersOpen(true)}
            aria-haspopup="dialog"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--s-1)',
              padding: '2px var(--s-2)',
              fontSize: 'var(--t-body)',
              border: '1px solid var(--hairline)',
              borderRadius: 2,
              background: activeFilterCount > 0 ? 'var(--color-accent-100)' : 'var(--surface)',
              color: 'var(--text-primary)',
              cursor: 'pointer',
            }}
          >
            <Filter size={12} />
            <span>{t('panels.screener.filters')}</span>
            {activeFilterCount > 0 && (
              <span
                data-numeric
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--t-meta)',
                  color: 'var(--accent)',
                }}
              >
                · {activeFilterCount}
              </span>
            )}
          </button>
          <button
            ref={columnsBtnRef}
            type="button"
            onClick={() => setColumnsOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={columnsOpen}
            title="Show, hide and reorder columns"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--s-1)',
              padding: '2px var(--s-2)',
              fontSize: 'var(--t-body)',
              border: '1px solid var(--hairline)',
              borderRadius: 2,
              background: columnsOpen ? 'var(--color-accent-100)' : 'var(--surface)',
              color: 'var(--text-primary)',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <Columns3 size={12} />
            <span>Columns</span>
          </button>
          <ColumnsMenu
            open={columnsOpen}
            onClose={() => setColumnsOpen(false)}
            anchorRef={columnsBtnRef}
            columns={columnConfig.columns}
            label={(id) => t(SCREENER_COLUMN_DEFS[id].labelKey)}
            actions={colActions}
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--s-1)',
              flex: 1,
              padding: '2px var(--s-2)',
              border: '1px solid var(--hairline)',
              borderRadius: 2,
              background: 'var(--menu-surface)',
              maxWidth: 360,
            }}
          >
            <Search size={12} style={{ opacity: 0.55 }} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('panels.screener.searchPlaceholder')}
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: 'var(--t-body)',
                color: 'var(--text-primary)',
              }}
            />
            {query && (
              <X
                size={10}
                onClick={() => setQuery('')}
                style={{ cursor: 'pointer', opacity: 0.6 }}
              />
            )}
          </div>
          {/* Methodology — opens the Methodology panel at the Revenue Estimate
              tab (Reported / Modelled / Dormant basis, the confidence P-value
              ranges, quarantine heuristics). Matches the labelled Methodology
              button on Referrals / Prevalence. */}
          <button
            type="button"
            onClick={() => openMethodology('revenue')}
            aria-label="Revenue methodology"
            title="How is Revenue and Revenue confidence calculated?"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--s-1)',
              padding: '2px var(--s-2)',
              fontSize: 'var(--t-body)',
              border: '1px solid var(--hairline)',
              borderRadius: 2,
              background: 'var(--surface)',
              color: 'var(--text-primary)',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <BookOpen size={12} />
            <span>Methodology</span>
          </button>
          <span
            data-numeric
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--t-meta)',
              color: 'var(--text-tertiary)',
              flexShrink: 0,
            }}
            title="Rows matching current filters / total in the universe"
          >
            {/* filtered (from server, exact) / universe total (from /screener/stats).
                Fall back to filtered for the denominator while stats loads. */}
            {total.toLocaleString('en-GB')}/{(statsQ.data?.total ?? total).toLocaleString('en-GB')}
          </span>
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={clearAll}
              style={{
                fontSize: 'var(--t-meta)',
                padding: '2px var(--s-1)',
                border: '1px solid var(--hairline)',
                borderRadius: 2,
                background: 'transparent',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
              }}
            >
              {t('panels.screener.clear')}
            </button>
          )}
        </div>

        <ScreenerFilterModalV2
          open={filtersOpen}
          onOpenChange={setFiltersOpen}
          categoricals={categoricals}
          setCategorical={setCategorical}
          ranges={ranges}
          setRange={setRange}
          facets={facetsQ.data ?? null}
          resultCount={rows.length}
          totalCount={total}
          onClearAll={clearAll}
        />

        <div
          style={{
            padding: 'var(--s-3)',
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <ScreenerTable
            columnConfig={columnConfig}
            colActions={colActions}
            rows={rows}
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={onSort}
            selectedId={selected}
            onSelect={select}
            onOpenOverview={openCompanyOverviewFor}
            isLoading={isLoading}
            error={!!error}
            hasNextPage={!!hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={() => {
              if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
            }}
            t={t}
          />
        </div>
      </div>
    </PanelFrame>
  );
}

// --- Two-pane table ---------------------------------------------------

interface TableProps {
  columnConfig: ScreenerColumnConfig;
  colActions: ScreenerColumnConfigActions;
  rows: ScreenerRow[];
  sortKey: SortKey;
  sortDir: 'asc' | 'desc';
  onSort: (k: SortKey) => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Double-click handler — opens a new floating Company Overview pinned
   *  to the row's entity. Separate from onSelect (which broadcasts to
   *  the channel) so single- and double-click stay independent. */
  onOpenOverview: (id: string) => void;
  isLoading: boolean;
  error: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  t: (key: string) => string;
}

function ScreenerTable(props: TableProps) {
  const {
    columnConfig,
    colActions,
    rows,
    sortKey,
    sortDir,
    onSort,
    selectedId,
    onSelect,
    onOpenOverview,
    t,
  } = props;
  const globalCurrency = useGlobalCurrency();
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const rightScrollRef = useRef<HTMLDivElement>(null);
  const leftBodyContentRef = useRef<HTMLDivElement>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => rightScrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  });

  // Right scroll → translate the left body so they stay aligned.
  useEffect(() => {
    const r = rightScrollRef.current;
    if (!r) return;
    const onScroll = () => {
      if (leftBodyContentRef.current) {
        leftBodyContentRef.current.style.transform = `translateY(-${r.scrollTop}px)`;
      }
    };
    onScroll();
    r.addEventListener('scroll', onScroll, { passive: true });
    return () => r.removeEventListener('scroll', onScroll);
  }, [rows.length]);

  useEffect(() => {
    if (!selectedId) return;
    const i = rows.findIndex((r) => r.entityId === selectedId);
    if (i >= 0) virtualizer.scrollToIndex(i, { align: 'auto' });
  }, [selectedId, rows, virtualizer]);

  // Infinite-scroll sentinel: re-observe on every render so the
  // sentinel ref attaches after the table mounts.
  useEffect(() => {
    if (!props.hasNextPage) return;
    const sentinel = loadMoreSentinelRef.current;
    const scrollRoot = rightScrollRef.current;
    if (!sentinel || !scrollRoot) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !props.isFetchingNextPage) props.onLoadMore();
        }
      },
      { root: scrollRoot, rootMargin: '200px 0px 0px 0px' },
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [props.hasNextPage, props.isFetchingNextPage, props.onLoadMore, rows.length]);

  if (props.isLoading) {
    return (
      <div style={{ padding: 'var(--s-4)', color: 'var(--text-tertiary)' }}>
        {t('panels.screener.loading')}
      </div>
    );
  }
  if (props.error) {
    return (
      <div style={{ padding: 'var(--s-4)', color: 'var(--color-negative-500)' }}>
        {t('panels.screener.error')}
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div style={{ padding: 'var(--s-4)', color: 'var(--text-tertiary)' }}>
        {t('panels.screener.empty')}
      </div>
    );
  }

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const visibleCols = columnConfig.columns.filter((c) => c.visible);
  const rowGridTemplate = visibleCols.map((c) => `${c.width}px`).join(' ');
  const rightMinWidth = visibleCols.reduce((sum, c) => sum + c.width, 0);

  return (
    <div
      onMouseLeave={() => setHoveredId(null)}
      style={{
        display: 'grid',
        gridTemplateColumns: `${columnConfig.nameWidth}px 1fr`,
        height: '100%',
        minHeight: 0,
      }}
    >
      {/* Frozen left pane */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          background: 'var(--surface)',
          borderRight: '1px solid var(--hairline)',
          boxShadow: '4px 0 6px -4px rgba(0,0,0,0.08)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <SortHeader
          label={t('panels.screener.columns.entity')}
          onClick={() => onSort('companyName')}
          isActive={sortKey === 'companyName'}
          dir={sortDir}
          width={columnConfig.nameWidth}
        />
        <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
          <div
            ref={leftBodyContentRef}
            style={{ height: totalSize, position: 'relative', willChange: 'transform' }}
          >
            {virtualItems.map((vi) => {
              const row = rows[vi.index]!;
              const isSelected = row.entityId === selectedId;
              const isHovered = !isSelected && row.entityId === hoveredId;
              return (
                <EntityRowContextMenu
                  key={vi.key}
                  entityId={row.entityId}
                  legalName={row.companyName ?? null}
                >
                  <div
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'link';
                      e.dataTransfer.setData(ENTITY_MIME, row.entityId);
                      e.dataTransfer.setData('text/plain', row.companyNumber);
                    }}
                    onMouseEnter={() => setHoveredId(row.entityId)}
                    onClick={() => onSelect(row.entityId)}
                    onDoubleClick={() => onOpenOverview(row.entityId)}
                    style={{
                      position: 'absolute',
                      top: vi.start,
                      left: 0,
                      right: 0,
                      height: vi.size,
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 var(--s-3)',
                      background: isSelected
                        ? 'var(--color-accent-100)'
                        : isHovered
                          ? 'var(--hover-subtle)'
                          : 'transparent',
                      borderBottom: '1px solid var(--border-subtle)',
                      cursor: 'pointer',
                      fontSize: 'var(--t-body)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    <strong
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                      }}
                    >
                      {row.companyName ?? '—'}
                    </strong>
                    {row.soleTrader && (
                      <span
                        title="Sole trader: a natural person with no separate legal entity — an asset deal (practice, patient list, equipment), never a share deal"
                        style={{
                          marginLeft: 'var(--s-2)',
                          flexShrink: 0,
                          fontSize: 'var(--t-micro, 10px)',
                          color: 'var(--text-secondary)',
                          border: '1px solid var(--border-subtle)',
                          borderRadius: 3,
                          padding: '0 4px',
                          lineHeight: '14px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Sole trader
                      </span>
                    )}
                  </div>
                </EntityRowContextMenu>
              );
            })}
          </div>
        </div>
      </div>

      {/* Scrollable right pane */}
      <div ref={rightScrollRef} style={{ overflow: 'auto', position: 'relative' }}>
        <div style={{ minWidth: rightMinWidth, position: 'relative' }}>
          {/* Headers — FT-shade fill to match the Watchlist panel's
              header (which renders via FrozenNameHeader / RightPaneHeader
              from entity-table-header.tsx with --surface-elevated). */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: rowGridTemplate,
              height: 22,
              borderBottom: '2px solid var(--text-primary)',
              background: 'var(--surface-elevated)',
              position: 'sticky',
              top: 0,
              zIndex: 2,
            }}
          >
            {visibleCols.map((col) => (
              <ColumnHeaderCell
                key={col.id}
                col={col}
                columns={columnConfig.columns}
                label={t(SCREENER_COLUMN_DEFS[col.id].labelKey)}
                numeric={SCREENER_COLUMN_DEFS[col.id].numeric}
                active={sortKey === col.id}
                sortDir={sortDir}
                onSort={onSort}
                moveColumn={colActions.moveColumn}
                setColumnWidth={colActions.setColumnWidth}
                tooltip={
                  col.id === 'region'
                    ? 'Registered-office region — sometimes an accountant’s address, not where the company operates.'
                    : undefined
                }
              />
            ))}
          </div>

          {/* Rows */}
          <div style={{ height: totalSize, position: 'relative' }}>
            {virtualItems.map((vi) => {
              const row = rows[vi.index]!;
              const isSelected = row.entityId === selectedId;
              const isHovered = !isSelected && row.entityId === hoveredId;
              return (
                <EntityRowContextMenu
                  key={vi.key}
                  entityId={row.entityId}
                  legalName={row.companyName ?? null}
                >
                  <div
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'link';
                      e.dataTransfer.setData(ENTITY_MIME, row.entityId);
                      e.dataTransfer.setData('text/plain', row.companyNumber);
                    }}
                    onClick={() => onSelect(row.entityId)}
                    onDoubleClick={() => onOpenOverview(row.entityId)}
                    onMouseEnter={() => setHoveredId(row.entityId)}
                    aria-selected={isSelected}
                    style={{
                      position: 'absolute',
                      top: vi.start,
                      left: 0,
                      right: 0,
                      height: vi.size,
                      display: 'grid',
                      gridTemplateColumns: rowGridTemplate,
                      fontSize: 'var(--t-body)',
                      borderBottom: '1px solid var(--border-subtle)',
                      background: isSelected
                        ? 'var(--color-accent-100)'
                        : isHovered
                          ? 'var(--hover-subtle)'
                          : 'transparent',
                      color: 'var(--text-primary)',
                      cursor: 'pointer',
                      alignItems: 'center',
                    }}
                  >
                    {visibleCols.map((col) => {
                      const def = SCREENER_COLUMN_DEFS[col.id];
                      const cell = renderCell(row, col.id, globalCurrency);
                      return (
                        <div
                          key={col.id}
                          data-numeric={def.numeric || undefined}
                          title={cell.suspectReason}
                          style={{
                            padding: '0 var(--s-2)',
                            textAlign: def.numeric ? 'right' : 'left',
                            fontFamily: def.numeric ? 'var(--font-mono)' : 'inherit',
                            fontVariantNumeric: def.numeric ? 'tabular-nums' : undefined,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            // Numbers stay full text-primary regardless of
                            // suspect state — the ⚠ marker carries the
                            // quality signal, the value itself stays
                            // readable and same colour as every other cell.
                          }}
                        >
                          {cell.suspect && (
                            <span
                              aria-label="suspect input"
                              style={{ color: 'var(--color-warning-500, #b45309)', marginRight: 4 }}
                            >
                              ⚠
                            </span>
                          )}
                          {cell.display}
                          {/* (cap) suffix removed from the Screener. The
                              size-class-ceiling explanation lives on the
                              Company Overview banner; the Screener stays
                              data-only so the cell row is scannable. */}
                        </div>
                      );
                    })}
                  </div>
                </EntityRowContextMenu>
              );
            })}
          </div>

          {(props.hasNextPage || props.isFetchingNextPage) && (
            <div
              ref={loadMoreSentinelRef}
              style={{
                padding: 'var(--s-3)',
                fontSize: 'var(--t-meta)',
                color: 'var(--text-tertiary)',
                textAlign: 'center',
              }}
            >
              {props.isFetchingNextPage ? t('panels.screener.loadingMore') : ''}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SortHeader({
  label,
  onClick,
  isActive,
  dir,
  width,
}: {
  label: string;
  onClick: () => void;
  isActive: boolean;
  dir: 'asc' | 'desc';
  width: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 4,
        height: 22,
        width,
        padding: '0 var(--s-3)',
        // FT-shade fill to match the right-pane header + the Watchlist
        // panel's column headers (entity-table-header uses --surface-elevated).
        background: 'var(--surface-elevated)',
        border: 'none',
        borderBottom: '2px solid var(--text-primary)',
        cursor: 'pointer',
        fontSize: 'var(--t-meta)',
        fontWeight: isActive ? 700 : 500,
        color: isActive ? 'var(--accent)' : 'var(--text-secondary)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
    >
      <span>{label}</span>
      {isActive && (dir === 'asc' ? <ChevronUp size={10} /> : <ChevronDown size={10} />)}
    </button>
  );
}

void FROZEN_NAME_DEFAULT_WIDTH;
void defaultScreenerColumnState;
